import {
  customFieldEntityTypePlural,
  customFieldEntityTypeSingular,
  type CustomFieldService,
  type TargetEntityType,
} from "@/shared/domain/customFields";
import { searchDeviceIds } from "@/shared/domain/devices";
import {
  QueryTarget,
  searchResourceIds,
  type ResourceIdPage,
  type ResourceIdQueryService,
} from "@/shared/domain/query";

import type { FetchSuggestionsProvider, SuggestionPage } from "./suggestions";

/**
 * How many suggestions one search asks for. A hint list, not a directory: the
 * author refines the search rather than scrolling past this.
 */
const SUGGESTION_PAGE_SIZE = 20;

/**
 * Shortest search a `CONTAINS` id search is sent: Postgres can extract no
 * trigram from a shorter `%term%`, so it would scan the whole index. Exact
 * matches (session and collection ids) are cheap at any length.
 */
const CONTAINS_MIN_SEARCH_LENGTH = 3;

function toSuggestionPage(page: ResourceIdPage): SuggestionPage {
  return { values: page.ids, truncated: page.truncated };
}

/**
 * Device ids in `orgId`, matched by substring (`device_id CONTAINS <term>`).
 * `hasUnset` is left out: whether a filtered row can lack a device depends on
 * the row, not on the device directory.
 */
export function deviceIdSuggestions(
  queryService: ResourceIdQueryService,
  orgId: string,
): FetchSuggestionsProvider {
  return {
    kind: "fetch",
    key: `device-ids:${orgId}`,
    minSearchLength: CONTAINS_MIN_SEARCH_LENGTH,
    fetch: async (search, signal) =>
      toSuggestionPage(
        await searchDeviceIds(queryService, orgId, search, {
          limit: SUGGESTION_PAGE_SIZE,
          signal,
        }),
      ),
  };
}

/** Dataset ids in `orgId`, matched by substring, newest first. */
export function datasetIdSuggestions(
  queryService: ResourceIdQueryService,
  orgId: string,
): FetchSuggestionsProvider {
  return {
    kind: "fetch",
    key: `dataset-ids:${orgId}`,
    minSearchLength: CONTAINS_MIN_SEARCH_LENGTH,
    fetch: async (search, signal) =>
      toSuggestionPage(
        await searchResourceIds(queryService, {
          orgId,
          target: QueryTarget.Datasets,
          idField: "dataset_id",
          match: "contains",
          sortBy: "created",
          sortDirection: "DESC",
          search,
          limit: SUGGESTION_PAGE_SIZE,
          signal,
        }),
      ),
  };
}

/**
 * Session ids in `orgId`, most recent first.
 *
 * Limited: the Sessions catalog admits only `EQUALS` / `NOT_EQUALS` on
 * `session_id`, so a typed term finds a session only when it spells the whole
 * id (useful to confirm a pasted id exists). The open, unsearched field lists
 * the most recent sessions.
 */
export function sessionIdSuggestions(
  queryService: ResourceIdQueryService,
  orgId: string,
): FetchSuggestionsProvider {
  return {
    kind: "fetch",
    key: `session-ids:${orgId}`,
    fetch: async (search, signal) =>
      toSuggestionPage(
        await searchResourceIds(queryService, {
          orgId,
          target: QueryTarget.Sessions,
          idField: "session_id",
          match: "equals",
          sortBy: "min_timestamp_ns",
          sortDirection: "DESC",
          search,
          limit: SUGGESTION_PAGE_SIZE,
          signal,
        }),
      ),
  };
}

/**
 * Collection ids in `orgId`, newest first.
 *
 * Limited like {@link sessionIdSuggestions}: `collection_id` admits only
 * `EQUALS` / `NOT_EQUALS`, so a typed term matches only a whole id.
 */
export function collectionIdSuggestions(
  queryService: ResourceIdQueryService,
  orgId: string,
): FetchSuggestionsProvider {
  return {
    kind: "fetch",
    key: `collection-ids:${orgId}`,
    fetch: async (search, signal) =>
      toSuggestionPage(
        await searchResourceIds(queryService, {
          orgId,
          target: QueryTarget.Collections,
          idField: "collection_id",
          match: "equals",
          sortBy: "created",
          sortDirection: "DESC",
          search,
          limit: SUGGESTION_PAGE_SIZE,
          signal,
        }),
      ),
  };
}

/** Which custom field's values to suggest. */
export interface CustomFieldValueSource {
  fieldId: string;
  /** The org that owns the field. */
  orgId: string;
  /** The kind of entity the field is defined on, which the summary counts. */
  entityType: TargetEntityType;
}

/** "1,250 distinct values across 4,321 sessions", singular where one. */
function describeValueTotals(
  distinctValues: number,
  entities: number,
  entityType: TargetEntityType,
): string {
  const valueNoun = distinctValues === 1 ? "distinct value" : "distinct values";
  const entityNoun =
    entities === 1
      ? customFieldEntityTypeSingular(entityType)
      : customFieldEntityTypePlural(entityType);
  return `${distinctValues.toLocaleString()} ${valueNoun} across ${entities.toLocaleString()} ${entityNoun}`;
}

/**
 * A String custom field's unique values containing the search, most common
 * first, summarized by the field's org-wide totals. `hasUnset` is left out: the
 * endpoint doesn't report whether any entity leaves the field unset.
 */
export function customFieldValueSuggestions(
  customFieldService: Pick<CustomFieldService, "getUniqueValues">,
  { fieldId, orgId, entityType }: CustomFieldValueSource,
): FetchSuggestionsProvider {
  return {
    kind: "fetch",
    key: `custom-field-values:${fieldId}`,
    fetch: async (search, signal) => {
      const response = await customFieldService.getUniqueValues(
        fieldId,
        { search, match: "substring", limit: SUGGESTION_PAGE_SIZE },
        { orgId, abortSignal: signal },
      );
      return {
        values: response.items.map((item) => item.value),
        truncated: response.truncated,
        counts: Object.fromEntries(
          response.items.map((item) => [item.value, item.count]),
        ),
        summary: describeValueTotals(
          response.distinct_values,
          response.entities_with_value,
          entityType,
        ),
      };
    },
  };
}
